import { createContext, useContext, useRef } from "react";
import type { ReactNode } from "react";
import type { PayoutStructure, SeasonManagerAliases, SeasonPayoutWinners } from "@averagejoesff/shared";
import { fetchManagerAliases, fetchPayoutWinners, fetchPayouts } from "../api/awards";
import { useResource } from "./useResource";
import type { Resource } from "./useResource";

/**
 * The admin-edited documents that more than one view reads at once: the
 * site-wide payout structure, plus the selected season's manager names and
 * prize winners.
 *
 * Each is loaded once here, above every page, and every reader and editor
 * shares that one copy. With a copy per component, a save updated only the
 * component that made it: renaming a manager in the Edit Season panel left
 * the stale label on the page underneath until a reload. And a page mounted
 * later re-fetched through the 60s CloudFront cache, which can still hold
 * the pre-save copy (see `useResource`). Saves push their response in
 * through the setters instead.
 */
interface LeagueDocs {
  payouts: Resource<PayoutStructure | null>;
  setPayouts: (payouts: PayoutStructure) => void;
  managerAliases: Resource<SeasonManagerAliases | null>;
  setManagerAliases: (aliases: SeasonManagerAliases) => void;
  payoutWinners: Resource<SeasonPayoutWinners | null>;
  setPayoutWinners: (winners: SeasonPayoutWinners) => void;
}

const LeagueDocsContext = createContext<LeagueDocs | null>(null);

export function LeagueDocsProvider({ season, children }: { season: number | null; children: ReactNode }) {
  const [payouts, setPayouts] = useResource(fetchPayouts, []);
  const [managerAliases, setAliasesResource] = useResource(
    () => (season === null ? Promise.resolve(null) : fetchManagerAliases(season)),
    [season],
  );
  const [payoutWinners, setWinnersResource] = useResource(
    () => (season === null ? Promise.resolve(null) : fetchPayoutWinners(season)),
    [season],
  );

  // A save captured its callback before it was sent, so its response can
  // arrive after the user picked another season. Compare against the latest
  // selection, never the one captured at save time.
  const selectedSeason = useRef(season);
  selectedSeason.current = season;
  function forSelectedSeason<T extends { season: number }>(set: (doc: T) => void) {
    return (doc: T) => {
      if (doc.season === selectedSeason.current) set(doc);
    };
  }

  const value: LeagueDocs = {
    payouts,
    setPayouts,
    managerAliases: onlyForSeason(managerAliases, season),
    setManagerAliases: forSelectedSeason<SeasonManagerAliases>(setAliasesResource),
    payoutWinners: onlyForSeason(payoutWinners, season),
    setPayoutWinners: forSelectedSeason<SeasonPayoutWinners>(setWinnersResource),
  };

  return <LeagueDocsContext.Provider value={value}>{children}</LeagueDocsContext.Provider>;
}

/**
 * Right after the season changes there's one render before the refetch
 * starts, when a resource still holds the previous season's document. Report
 * that as loading, so no reader shows (or an edit form starts from) another
 * season's data.
 */
function onlyForSeason<T extends { season: number }>(
  resource: Resource<T | null>,
  season: number | null,
): Resource<T | null> {
  const stale = resource.status === "ready" && resource.data !== null && resource.data.season !== season;
  return stale ? { status: "loading" } : resource;
}

export function useLeagueDocs(): LeagueDocs {
  const ctx = useContext(LeagueDocsContext);
  if (!ctx) throw new Error("useLeagueDocs must be used within a LeagueDocsProvider");
  return ctx;
}
